import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Row } from '@components/form/Form/Form';
import { safelyParseJson } from '@utils/utils';
import { serviceApi } from '@api/serviceApi';
import './road-route-checker.scss';
import { PtElement, PtRouteType } from '@api/entities/replancity_RoadInfrastructureRoute';
import { useToastContext } from '@context/toastContext';
import { EntityServiceName } from '@api/enums/enums';
import * as TextField from '@components/form/TextField/TextField';
import { PublicTransitLayerEntityName } from '@api/entities/replancity_PublicTransitLine';
import RouteSegmentCard from './RouteSegmentCard';
import RouteCheckerBase from '@components/RouteCheckerBase/RouteCheckerBase';

const RouteChecker = ({
    entityServiceName,
    formFields,
}: {
    entityServiceName: EntityServiceName;
    formFields?: React.ReactNode;
}) => {
    const { t } = useTranslation();
    const { addToast } = useToastContext();

    const getRouteDetails = useCallback(
        async ({ entityServiceName, presetId, formDataObj, abortSignal }) => {
            const fromPoint = JSON.parse(formDataObj.fromPoint as string);
            const toPoint = JSON.parse(formDataObj.toPoint as string);

            return serviceApi.calculateAndGetPtRouteGeometry(
                entityServiceName,
                {
                    presetId,
                    fromLon: fromPoint.lng,
                    fromLat: fromPoint.lat,
                    toLon: toPoint.lng,
                    toLat: toPoint.lat,
                    startTime: formDataObj.startTime as string,
                } as any,
                abortSignal
            );
        },
        []
    );

    const renderResult = useCallback(
        (routeDetails: PtRouteType) => (
            <>
                {routeDetails.isFromCache ? (
                    <div className="segment-card__line">
                        Route has been calculated using cache and may not contain latest changes,
                        cache live time is 3 min after last routing request!
                    </div>
                ) : null}
                {routeDetails.elements?.length ? (
                    <div className="road-route-checker__segments">
                        {routeDetails.elements.map((segment, ind) => (
                            <RouteSegmentCard key={ind} {...segment} />
                        ))}
                    </div>
                ) : null}
            </>
        ),
        []
    );

    const getFeatureStateIds = useCallback(
        routeDetails =>
            routeDetails.elements
                ?.filter((el): el is PtElement => el.mode === 'pt')
                ?.flatMap(({ featureStateIdsList }) => featureStateIdsList) ?? [],
        []
    );

    const errorHandler = useCallback(resp => {
        try {
            const parsedErr =
                typeof resp.error === 'string'
                    ? safelyParseJson(resp.error)?.error
                    : resp.error.error;
            addToast(parsedErr, 'error');
        } catch (e) {
            console.log('Replan. Error parsing error response', resp, e);
        }
    }, []);

    return (
        <RouteCheckerBase<PtRouteType>
            title={t('route-check.header-title')}
            submitBtnTitle={t('route-check.form-submit-btn')}
            layerEntityName={PublicTransitLayerEntityName.ROUTES}
            entityServiceName={entityServiceName}
            formFields={
                <>
                    <Row>
                        <TextField.default
                            name="startTime"
                            label={t('transit-lines.departure-from')}
                            type="time"
                            step="1"
                            required={true}
                            style={{ width: 'maxContent' }}
                            autoFocus
                        />
                    </Row>
                    {formFields}
                </>
            }
            getRouteDetails={getRouteDetails}
            getFeatureStateIds={getFeatureStateIds}
            onError={errorHandler}
            renderResult={renderResult}
        />
    );
};

export default RouteChecker;
